import * as React from 'react';
import i18n from 'i18next';
import _set from 'lodash/set';
import _merge from 'lodash/merge';
import { action } from 'mobx';

// types
import { Widget } from '@sprinklr/stories/widget/Widget';
import { RankedTableWidgetOptionsImpl } from './options';
import { Theme } from 'models/Theme/Theme';
import { AnalyticsProjection } from '@sprinklr/stories/analytics/AnalyticsRequest';
import { NumberFormat } from '../DataTableWidget/types';

// components
import ActionCheckboxSliderGroup from 'components/_UI/Forms/Actions/ActionCheckboxSliderGroup';
import ActionCheckboxGroup from 'components/_UI/Forms/Actions/ActionCheckboxGroup';
import ActionSliderGroup from 'components/_UI/Forms/Actions/ActionSliderGroup';
import ActionSelectGroup from 'components/_UI/Forms/Actions/ActionSelectGroup';
import ActionSegmentedGroup from 'components/_UI/Forms/Actions/ActionSegmentedGroup';
import ActionCheckboxSliderColorSwatchGroup from 'components/_UI/Forms/Actions/ActionCheckboxSliderColorSwatchGroup';
import { CollapseProps, TunablesCollapse } from 'components/Sidebar/RenderTunables/RenderTunables';

// constants
import {
    horizontalAlignOptions,
    X_AXIS_OPTIONS,
} from 'components/Panel/PanelEditorSidebar/PanelDesign/PanelDesignOptions';
import { METRIC_VALUE_FORMAT_VS_NUMBER_FORMAT } from './constants';

// helpers

// tunables
import {
    headerTunables,
    metricValueTunables,
    opacityTunables,
    percentChangeTunables,
    smartSizeTunables,
    rtlTunables,
    themeTunables,
} from 'components/Sidebar/RenderTunables/SharedTunables';
import { getMetricDesignOptionsKey } from 'components/Sidebar/RenderTunables/metricDesignTunables/utils/getMetricDesignOptionsKey';
import { MetricDesignTunables } from 'components/Sidebar/RenderTunables/metricDesignTunables';

export const getFallbackColor = (theme: Theme, mergedTheme: Theme): string => {
    return theme?.typography?.color || mergedTheme?.typography?.color || '';
};

export function getMetricOptionsWithFallback(
    widget: Widget
): (metric: AnalyticsProjection) => NumberFormat {
    const options = widget.options?.metricDesignOptions;
    const fallbackValueFormat =
        METRIC_VALUE_FORMAT_VS_NUMBER_FORMAT[widget.options.metricValueFormat];

    return function(metric: AnalyticsProjection): NumberFormat {
        // merge into empty object so we don't overwrite fallbackValueFormat
        return _merge(
            {},
            fallbackValueFormat,
            options?.[getMetricDesignOptionsKey(metric.measurementName, metric.aggregateFunction)]
                ?.numberFormat ?? {}
        );
    };
}

const RankedTableTunables = (widget: Widget, mergedTheme: Theme): TunablesCollapse[] => [
    {
        props: {
            label: i18n.t('Table'),
            startOpen: true,
        } as CollapseProps,
        children: [
            ...headerTunables(widget),
            opacityTunables(widget),
            smartSizeTunables(widget),
            rtlTunables(widget),
        ],
    },
    {
        props: {
            label: widget.options.invertDataSet ? i18n.t('Rows') : i18n.t('Columns'),
            startOpen: true,
        } as CollapseProps,
        children: [
            <MetricDesignTunables
                key='metric-design'
                getMetricOptionsMapFn={getMetricOptionsWithFallback}
                widget={widget}
                options={{ colorTag: true }}
            />,
        ],
    },
    {
        props: {
            label: i18n.t('Labels'),
            startOpen: true,
        } as CollapseProps,
        children: [
            widget.options.invertDataSet && (
                <ActionSliderGroup
                    key='widget.options.labelSize'
                    label={i18n.t('Row Name')}
                    option={widget.options}
                    value='labelSize'
                    showValue
                    min={0}
                    max={100}
                />
            ),
            <ActionCheckboxGroup
                key='widget.options.autoRowHeight'
                label={i18n.t('Auto Distribute Row Height')}
                option={widget.options}
                value='autoRowHeight'
            />,
            !(widget.options as RankedTableWidgetOptionsImpl).autoRowHeight && (
                <ActionCheckboxSliderGroup
                    key='widget.options.rowSize'
                    label={i18n.t('Row Size')}
                    showValue
                    checkbox={{
                        option: widget.options,
                        value: 'showRowSize',
                    }}
                    slider={{
                        option: widget.options,
                        value: 'rowSize',
                        min: 0,
                        max: 100,
                    }}
                />
            ),
            <ActionCheckboxGroup
                key='widget.options.rowNumbers'
                label={i18n.t('Show Row Numbers')}
                option={widget.options}
                value='rowNumbers'
            />,
            <ActionCheckboxSliderGroup
                key='widget.options.showColumnName'
                label={i18n.t('Column Name')}
                showValue
                checkbox={{
                    option: widget.options,
                    value: 'showColumnName',
                }}
                slider={{
                    option: widget.options,
                    value: 'columnNameSize',
                    min: 0,
                    max: 50,
                }}
            />,
            <ActionSegmentedGroup
                key='widget.options.alignment.columnName'
                label={i18n.t('Column Name Align')}
                option={(widget.options as any).alignment}
                value={'columnName'}
                options={horizontalAlignOptions}
            />,
            <ActionSliderGroup
                key='widget.options.dimensionSize'
                label={i18n.t('Dimension Size')}
                option={widget.options}
                value='dimensionSize'
                showValue
                min={0}
                max={100}
            />,

            <ActionSegmentedGroup
                key='widget.options.alignment.rowName'
                label={i18n.t('Dimension Align')}
                option={(widget.options as any).alignment}
                value={'rowName'}
                options={horizontalAlignOptions}
            />,
            <ActionSelectGroup
                key='widget.options.columnNameFormat'
                label={i18n.t('Date Format')}
                option={widget.options}
                value='columnNameFormat'
                valueKey='valueMoment'
                options={X_AXIS_OPTIONS}
            />,
            ...metricValueTunables(widget, {
                value: { enable: true, size: true },
                format: true,
                name: true,
                change: true,
            }),
            <ActionSegmentedGroup
                key='widget.options.alignment.metric'
                label={i18n.t('Metric Align')}
                option={(widget.options as any).alignment}
                value={'metric'}
                options={horizontalAlignOptions}
            />,
            ...percentChangeTunables(widget, mergedTheme, {
                highlight: true,
                format: true,
                color: true,
            }),

            <ActionCheckboxSliderColorSwatchGroup
                key='widget.border.cell'
                label={i18n.t('Cell Borders')}
                showValue
                checkbox={{
                    option: (widget.options as RankedTableWidgetOptionsImpl)?.borders?.cell,
                    value: 'enabled',
                }}
                slider={{
                    option: (widget.options as RankedTableWidgetOptionsImpl)?.borders?.cell,
                    value: 'size',
                    min: 1,
                    max: 100,
                }}
                colorSwatch={{
                    option: null,
                    value: null,
                    onClear: action(
                        () =>
                            ((widget.options as RankedTableWidgetOptionsImpl).borders.cell.color =
                                '')
                    ),
                    onChange: action(
                        ({ rgb: { r, g, b, a } }: any) =>
                            ((widget.options as RankedTableWidgetOptionsImpl).borders.cell.color = `rgba(${r}, ${g}, ${b}, ${a})`)
                    ),
                    color: !!(widget.options as RankedTableWidgetOptionsImpl)?.borders?.cell.color
                        ? (widget.options as RankedTableWidgetOptionsImpl)?.borders?.cell.color
                        : getFallbackColor(widget.theme, mergedTheme),
                    secondaryIcon: !!(widget.options as RankedTableWidgetOptionsImpl)?.borders?.cell
                        .color
                        ? 'icon-undo'
                        : undefined,
                }}
            />,
            !(widget.options as RankedTableWidgetOptionsImpl)?.borders?.cell?.enabled ? (
                <ActionCheckboxSliderColorSwatchGroup
                    key='widget.border.top'
                    label={i18n.t('Border Top')}
                    showValue
                    checkbox={{
                        option: (widget.options as RankedTableWidgetOptionsImpl)?.borders?.top,
                        value: 'enabled',
                    }}
                    slider={{
                        option: (widget.options as RankedTableWidgetOptionsImpl)?.borders?.top,
                        value: 'size',
                        min: 1,
                        max: 100,
                    }}
                    colorSwatch={{
                        option: null,
                        value: null,
                        onClear: action(
                            () =>
                                ((widget.options as RankedTableWidgetOptionsImpl).borders.top.color =
                                    '')
                        ),
                        onChange: action(
                            ({ rgb: { r, g, b, a } }: any) =>
                                ((widget.options as RankedTableWidgetOptionsImpl).borders.top.color = `rgba(${r}, ${g}, ${b}, ${a})`)
                        ),
                        color: !!(widget.options as RankedTableWidgetOptionsImpl)?.borders?.top
                            .color
                            ? (widget.options as RankedTableWidgetOptionsImpl)?.borders?.top.color
                            : getFallbackColor(widget.theme, mergedTheme),
                        secondaryIcon: !!(widget.options as RankedTableWidgetOptionsImpl)?.borders
                            ?.top.color
                            ? 'icon-undo'
                            : undefined,
                    }}
                />
            ) : null,
            !(widget.options as RankedTableWidgetOptionsImpl)?.borders?.cell?.enabled ? (
                <ActionCheckboxSliderColorSwatchGroup
                    key='widget.points.value'
                    label={i18n.t('Border Bottom')}
                    showValue
                    disabled={
                        (widget.options as RankedTableWidgetOptionsImpl)?.borders?.cell?.enabled
                    }
                    checkbox={{
                        option: (widget.options as RankedTableWidgetOptionsImpl)?.borders?.bottom,
                        value: 'enabled',
                    }}
                    slider={{
                        option: (widget.options as RankedTableWidgetOptionsImpl)?.borders?.bottom,
                        value: 'size',
                        min: 1,
                        max: 100,
                    }}
                    colorSwatch={{
                        option: null,
                        value: null,
                        onClear: action(
                            () =>
                                ((widget.options as RankedTableWidgetOptionsImpl).borders.bottom.color =
                                    '')
                        ),
                        onChange: action(
                            ({ rgb: { r, g, b, a } }: any) =>
                                ((widget.options as RankedTableWidgetOptionsImpl).borders.bottom.color = `rgba(${r}, ${g}, ${b}, ${a})`)
                        ),
                        color: !!(widget.options as RankedTableWidgetOptionsImpl)?.borders?.bottom
                            .color
                            ? (widget.options as RankedTableWidgetOptionsImpl)?.borders?.bottom
                                  .color
                            : getFallbackColor(widget.theme, mergedTheme),
                        secondaryIcon: !!(widget.options as RankedTableWidgetOptionsImpl)?.borders
                            ?.bottom.color
                            ? 'icon-undo'
                            : undefined,
                    }}
                />
            ) : null,
        ],
    },
    themeTunables(widget, { color: true, image: true, email: true, imageSize: true }),
    {
        props: {
            label: i18n.t('Tunables'),
            startOpen: true,
        } as CollapseProps,
        children: [
            <ActionCheckboxGroup
                key='widget.options.invertDataSet'
                label={i18n.t('Flip Rows/Columns')}
                option={widget.options}
                value='invertDataSet'
            />,
        ],
    },
];

export default RankedTableTunables;
